import axios from 'axios';
import {
  resolvePositionAssignments,
  type ActivatedUserPayload,
} from 'components/IncidentConfig/CreateIncident/createIncidentModel';
import { DEFAULT_ICS_FORM_FAMILY } from 'components/IAP/formFamily';
import {
  defaultDraftSeats,
  rosterNodesForCreate,
} from 'components/IncidentConfig/CreateIncident/draftRosterModel';
import type { Incident } from 'components/IAP/hooks/incidentHooks';
import { DEFAULT_ACTION_CLASSIFICATION } from 'components/IAP/actionClassification';
import {
  OBJECTIVE_TYPE_MANAGEMENT,
  OBJECTIVE_TYPE_OPERATIONAL,
} from 'components/IAP/incidentObjectiveTypes';
import { DEFAULT_ACTION_PRIORITY } from 'components/IAP/ICSConstants';
import config from 'constants/apiConfig';
import { INITIAL_RESPONSE_VIEW_PARAM } from 'utils/initialResponseView';

/** Stable Hub name for the FOC 8.x unescalated Initial Response demo. */
export const FOC_DEMO_INITIAL_RESPONSE_NAME = 'FOC Demo — Initial Response';

/** Keep in sync with FOC_INITIAL_RESPONSE_TOUR_PARAM (avoid circular import). */
const FOC_INITIAL_RESPONSE_TOUR_PARAM = 'focInitialResponseTour';

export type FocInitialResponseIncident = {
  id: string;
  name?: string | null;
  archived?: number | boolean | null;
  escalated_at?: string | null;
};

const isLive = (incident: FocInitialResponseIncident) => !incident.archived;
const isUnescalated = (incident: FocInitialResponseIncident) =>
  isLive(incident) && !incident.escalated_at;

export function findFocInitialResponseDemo(
  incidents: readonly FocInitialResponseIncident[] = []
): FocInitialResponseIncident | null {
  const demo = incidents.find(
    (incident) =>
      isUnescalated(incident) &&
      String(incident.name ?? '').trim() === FOC_DEMO_INITIAL_RESPONSE_NAME
  );
  return demo ?? null;
}

/**
 * Preference for 8.x Hub resolve:
 * 1) named FOC IR demo, 2) current if unescalated, 3) first live unescalated.
 */
export function preferredFocInitialResponseId(
  incidents: readonly FocInitialResponseIncident[] = [],
  currentId?: string | null
): string | null {
  const demo = findFocInitialResponseDemo(incidents);
  if (demo) return String(demo.id);

  if (currentId) {
    const current = incidents.find(
      (incident) =>
        String(incident.id) === String(currentId) && isUnescalated(incident)
    );
    if (current) return String(current.id);
  }

  const live = incidents.find(isUnescalated);
  return live ? String(live.id) : null;
}

export type EnsureFocInitialResponseDemoDeps = {
  incidents: readonly FocInitialResponseIncident[];
  groupGuid: string;
  user: {
    user_guid: string;
    name?: string | null;
    email_address?: string | null;
  };
  createIncident: (payload: {
    name: string;
    group_guid: string;
    areas_of_responsibility: string[];
    incident_types: string[];
    ics_form_family: string;
    activated_users: ActivatedUserPayload[];
    roster_nodes: ReturnType<typeof rosterNodesForCreate>;
  }) => Promise<Pick<Incident, 'id' | 'name'>>;
};

/** True when the incident has no active objectives (safe to seed demo OA). */
export async function focInitialResponseDemoNeedsOaSeed(
  incidentId: string
): Promise<boolean> {
  const { data } = await axios.get(
    `${config.apiGateway.ics_incident_objectives}/${incidentId}`
  );
  const rows = Array.isArray(data) ? data : [];
  return rows.length === 0;
}

/**
 * Seeds two objectives and two actions so the 8.3 OA walk has real rows.
 * Best-effort; failures must not block the tour.
 */
export async function seedFocInitialResponseDemoObjectivesActions(deps: {
  incidentId: string;
  groupGuid: string;
  userGuid: string;
}): Promise<void> {
  const { incidentId, groupGuid, userGuid } = deps;
  if (!incidentId || !groupGuid || !userGuid) return;

  await axios.post(`${config.apiGateway.ics_objectives}`, {
    name: 'FOC demo — stabilize scene',
    type: OBJECTIVE_TYPE_OPERATIONAL,
    group_guid: groupGuid,
    user_guid: userGuid,
    incident_id: incidentId,
  });
  await axios.post(`${config.apiGateway.ics_objectives}`, {
    name: 'FOC demo — coordinate stakeholders',
    type: OBJECTIVE_TYPE_MANAGEMENT,
    group_guid: groupGuid,
    user_guid: userGuid,
    incident_id: incidentId,
  });

  const actionBase = {
    checklist: [] as { label: string; checked: boolean }[],
    archived: false,
    priority: DEFAULT_ACTION_PRIORITY,
    classification: DEFAULT_ACTION_CLASSIFICATION,
    briefed_poc: false,
    parent_id: null,
    parentId: null,
    assignee_kind: null,
    assignee_id: null,
    poc: null,
    aor_id: null,
    group_guid: groupGuid,
    user_guid: userGuid,
    incident_id: incidentId,
  };

  await axios.post(`${config.apiGateway.ics_actions}`, {
    ...actionBase,
    name: 'FOC demo — establish ICP',
    description: 'Set up the initial command post and communications.',
    status: 'Current/Ongoing',
  });
  await axios.post(`${config.apiGateway.ics_actions}`, {
    ...actionBase,
    name: 'FOC demo — brief incoming resources',
    description: 'Plan the next wave of resource briefings.',
    status: 'Planned',
  });
}

/** Seed OA when the demo has zero objectives; swallow errors so the tour can open. */
async function seedOaIfNeeded(deps: {
  incidentId: string;
  groupGuid: string;
  userGuid: string;
}): Promise<void> {
  try {
    const needsSeed = await focInitialResponseDemoNeedsOaSeed(deps.incidentId);
    if (!needsSeed) return;
    await seedFocInitialResponseDemoObjectivesActions(deps);
  } catch {
    // Tour still opens; OA steps fall back to empty-state UI.
  }
}

/**
 * Returns an unescalated demo incident id, creating a Training workspace with
 * the current user as Incident Commander when the named demo is missing.
 * Seeds objectives/actions when the demo is new or has zero objectives.
 */
export async function ensureFocInitialResponseDemoWorkspace(
  deps: EnsureFocInitialResponseDemoDeps
): Promise<string | null> {
  const existing = findFocInitialResponseDemo(deps.incidents);
  if (existing) {
    const incidentId = String(existing.id);
    if (deps.groupGuid && deps.user.user_guid) {
      await seedOaIfNeeded({
        incidentId,
        groupGuid: deps.groupGuid,
        userGuid: deps.user.user_guid,
      });
    }
    return incidentId;
  }

  if (!deps.groupGuid || !deps.user.user_guid) return null;

  const seats = defaultDraftSeats();
  const activatedUser: ActivatedUserPayload = {
    user_guid: deps.user.user_guid,
    role: 'Incident Commander',
    email_address: deps.user.email_address || '',
    name: deps.user.name || deps.user.email_address || 'FOC Demo User',
  };

  const created = await deps.createIncident({
    name: FOC_DEMO_INITIAL_RESPONSE_NAME,
    group_guid: deps.groupGuid,
    areas_of_responsibility: [],
    incident_types: ['Training'],
    ics_form_family: DEFAULT_ICS_FORM_FAMILY,
    activated_users: [activatedUser],
    roster_nodes: rosterNodesForCreate(seats),
  });

  const incidentId = created?.id ? String(created.id) : null;
  if (!incidentId) return null;

  try {
    const { addUsersToIncidentRoster, getPositionsForIncident } = await import(
      'hooks/incident/useRoster'
    );
    const positions = await getPositionsForIncident(incidentId);
    const positionAssignments = resolvePositionAssignments(
      [
        {
          user_guid: deps.user.user_guid,
          name: activatedUser.name,
          email_address: activatedUser.email_address,
          position: 'Incident Commander',
        },
      ],
      positions
    );
    if (positionAssignments.length > 0) {
      await addUsersToIncidentRoster({
        incidentId,
        groupGuid: deps.groupGuid,
        positionAssignments,
      });
    }
  } catch {
    // Creator remains an activated member.
  }

  await seedOaIfNeeded({
    incidentId,
    groupGuid: deps.groupGuid,
    userGuid: deps.user.user_guid,
  });

  return incidentId;
}

export function focInitialResponseDemoHref(incidentId: string): string {
  return `/iap/${incidentId}/initial-response?${INITIAL_RESPONSE_VIEW_PARAM}=new&${FOC_INITIAL_RESPONSE_TOUR_PARAM}=1`;
}
